import React, { AllHTMLAttributes, CSSProperties } from 'react';
import cx from 'classnames';
import AkTextfield, {
  ThemeProps as TextfieldThemeProps,
} from '@atlaskit/textfield';
import {
  GLOBAL_NAMESPACE_PREFIX,
  ComponentAppearanceDefault as DEFAULT,
  ComponentAppearanceSubtle as SUBTLE,
  ComponentAppearanceNone as NONE,
  ComponentClassnameStatic,
  ComponentSizeXs as XSMALL,
  ComponentSizeS as SMALL,
  ComponentSizeM as MEDIUM,
  ComponentSizeL as LARGE,
  ComponentSizeXl as XLARGE,
  ComponentStateFocus as FOCUS,
  ComponentStateDisabled as DISABLED,
  ComponentStateHover as HOVER,
  ComponentStateInvalid as INVALID,
  TextfieldClassnameBase,
  TextfieldDefaultBackgroundColor,
  TextfieldDefaultBoxShadowWidth,
  TextfieldDefaultBoxShadowColor,
  TextfieldDefaultColor,
  TextfieldDefaultPaddingY,
  TextfieldDefaultPaddingX,
  TextfieldDefaultLineHeight,
  TextfieldDefaultTransition,
  TextfieldPlaceholderDefaultColor,
  TextfieldDefaultHoverBackgroundColor,
  TextfieldDefaultHoverBoxShadowColor,
  TextfieldDefaultFocusBackgroundColor,
  TextfieldDefaultFocusBoxShadowColor,
  TextfieldDefaultInvalidBoxShadowColor,
  TextfieldDefaultDisabledBackgroundColor,
  TextfieldDefaultDisabledBoxShadowColor,
  TextfieldDefaultMaxHeight,
  TextfieldSubtleBackgroundColor,
  TextfieldDefaultFontSize,
  ComponentClassnameLabel,
  TextfieldSubtleBoxShadowColor,
  TextfieldPlaceholderSubtleColor,
  TextfieldSubtleColor,
  TextfieldSubtleHoverBackgroundColor,
  TextfieldSubtleFocusBackgroundColor,
  TextfieldNoneBackgroundColor,
  TextfieldNoneBoxShadowColor,
  TextfieldNoneHoverBackgroundColor,
  TextfieldNoneFocusBackgroundColor,
} from '../../../tokens';

import styles from './Textfield.less';

// TODO: @jchung - add htmlFor, name, and id attributes
// to associate input with label
export const TextfieldClasses = {
  BASE: `${GLOBAL_NAMESPACE_PREFIX}${TextfieldClassnameBase}`,
  LABEL: `${GLOBAL_NAMESPACE_PREFIX}${TextfieldClassnameBase}__${ComponentClassnameLabel}`,
  // For usage with static textfield
  STATIC: `${GLOBAL_NAMESPACE_PREFIX}${TextfieldClassnameBase}--${ComponentClassnameStatic}`,
};

// see component render function for notes of why this extra appearance
const STANDARD: 'standard' = 'standard';
type TextfieldAppearance =
  | typeof DEFAULT
  | typeof NONE // input appears without border, bg, etc. but still functional
  | typeof SUBTLE; // input has no bg when idle, but still has hover and focus states, for example Board Create menu
type AkTextfieldAppearance = typeof STANDARD | typeof NONE | typeof SUBTLE;

type TextfieldState =
  | typeof DEFAULT
  | typeof DISABLED
  | typeof FOCUS
  | typeof HOVER
  | typeof INVALID;

// https://bitbucket.org/atlassian/atlaskit-mk-2/src/master/packages/core/textfield/src/theme.ts#packages/core/textfield/src/theme.ts-193
type TextfieldWidthValue =
  | typeof XSMALL // 80px
  | typeof SMALL // 160px
  | typeof MEDIUM // 240px
  | typeof LARGE // 320px
  | typeof XLARGE; // 480px

type TextfieldStyleDict = {
  readonly [Appearance in TextfieldAppearance]: {
    readonly [State in TextfieldState]?: {
      readonly [Prop in keyof CSSProperties]: string;
    };
  } & { readonly default: CSSProperties }; // must have default state styles
};

type TextfieldLabelType = React.ReactNode | string | null;

// textfield state styles MUST have a 'default' key
// of set properties for a fallback
const textfieldStyleDict: TextfieldStyleDict = {
  [DEFAULT]: {
    [DEFAULT]: {
      backgroundColor: TextfieldDefaultBackgroundColor,
      boxShadow: `inset 0 0 0 ${TextfieldDefaultBoxShadowWidth} ${TextfieldDefaultBoxShadowColor}`,
      color: TextfieldDefaultColor,
      fontSize: TextfieldDefaultFontSize,
      padding: `${TextfieldDefaultPaddingY} ${TextfieldDefaultPaddingX}`,
      lineHeight: TextfieldDefaultLineHeight,
      transition: TextfieldDefaultTransition,
    },
    [HOVER]: {
      backgroundColor: TextfieldDefaultHoverBackgroundColor,
      boxShadow: `inset 0 0 0 ${TextfieldDefaultBoxShadowWidth} ${TextfieldDefaultHoverBoxShadowColor}`,
    },
    [FOCUS]: {
      backgroundColor: TextfieldDefaultFocusBackgroundColor,
      boxShadow: `inset 0 0 0 ${TextfieldDefaultBoxShadowWidth} ${TextfieldDefaultFocusBoxShadowColor}`,
    },
    [INVALID]: {
      boxShadow: `inset 0 0 0 ${TextfieldDefaultBoxShadowWidth} ${TextfieldDefaultInvalidBoxShadowColor}`,
    },
    [DISABLED]: {
      backgroundColor: TextfieldDefaultDisabledBackgroundColor,
      boxShadow: `inset 0 0 0 ${TextfieldDefaultBoxShadowWidth} ${TextfieldDefaultDisabledBoxShadowColor}`,
    },
  },
  [SUBTLE]: {
    [DEFAULT]: {
      backgroundColor: TextfieldSubtleBackgroundColor,
      boxShadow: TextfieldSubtleBoxShadowColor,
      color: TextfieldSubtleColor,
    },
    [HOVER]: {
      backgroundColor: TextfieldSubtleHoverBackgroundColor,
    },
    [FOCUS]: {
      backgroundColor: TextfieldSubtleFocusBackgroundColor,
    },
  },
  [NONE]: {
    [DEFAULT]: {
      backgroundColor: TextfieldNoneBackgroundColor,
      boxShadow: TextfieldNoneBoxShadowColor,
    },
    [HOVER]: {
      backgroundColor: TextfieldNoneHoverBackgroundColor,
      boxShadow: TextfieldNoneBoxShadowColor,
    },
    [FOCUS]: {
      backgroundColor: TextfieldNoneFocusBackgroundColor,
      boxShadow: TextfieldNoneBoxShadowColor,
    },
  },
};

const textfieldPlaceholderStyleDict: TextfieldStyleDict = {
  [DEFAULT]: {
    [DEFAULT]: {
      color: TextfieldPlaceholderDefaultColor,
    },
  },
  [SUBTLE]: {
    [DEFAULT]: {
      color: TextfieldPlaceholderSubtleColor,
    },
  },
  [NONE]: {
    [DEFAULT]: {},
    [HOVER]: {},
  },
};

// this logic is to prevent errors occuring when we try to look up
// an appearance or an appearance-state pairing that doesn't have a
// corresponding style property
const getTextfieldStyleProp = (
  appearance: TextfieldAppearance,
  state: TextfieldState,
  prop: keyof CSSProperties,
  styleDict: TextfieldStyleDict = textfieldStyleDict,
) => {
  const defaultAppearanceStyles = styleDict[DEFAULT];
  const defaultStateStyles = styleDict[DEFAULT][DEFAULT];

  // if the button styles have the appearance, take on that object for styles.
  // Otherwise, fallback to the default.
  const appearanceStyles = styleDict[appearance] || defaultAppearanceStyles;

  // if the appearance AND corresponding state exist, use those styles.
  // If the appearance does NOT have the corresponding state,
  // use the styles that the default appearance has for that state
  const appearanceStateStyles =
    appearanceStyles[state] || defaultAppearanceStyles[state];

  // if we have all the proper entries for the styles, return the style property
  // if we DON'T have a style to return, return the default styles of the
  // appearance
  // if there are no default styles for the appearance, fallback to the default
  // appearance and default state.
  return appearanceStateStyles![prop]
    ? appearanceStateStyles![prop]
    : appearanceStyles[DEFAULT][prop]
    ? appearanceStyles[DEFAULT][prop]
    : defaultAppearanceStyles![state]![prop]
    ? defaultAppearanceStyles![state]![prop]
    : defaultStateStyles[prop];
};

const customTheme = (
  // normally we would type the function params too, but we run
  // into an error because our version of React.CSSProperties doesn't match
  // @atlaskit/textfield at this time
  // eslint-disable-next-line  @typescript-eslint/ban-types
  currentTheme: Function,
  themeProps: TextfieldThemeProps,
) => {
  const { container: containerStyles, input: inputStyles } = currentTheme(
    themeProps,
  );
  const {
    appearance,
    isDisabled,
    isInvalid,
    isFocused,
    isHovered,
  } = themeProps;
  let state: TextfieldState = DEFAULT;

  if (isHovered) {
    state = HOVER;
  }
  if (isFocused) {
    state = FOCUS;
  }
  if (isInvalid) {
    state = INVALID;
  }
  if (isDisabled) {
    state = DISABLED;
  }

  // see component render function for notes of why this typecasting
  let convertedAppearance:
    | TextfieldAppearance
    | AkTextfieldAppearance
    | undefined = appearance;
  if (appearance === STANDARD) {
    convertedAppearance = DEFAULT;
  }

  // take out styles we don't need here
  const {
    // eslint-disable-next-line  @typescript-eslint/no-unused-vars
    borderColor, // we use box shadow instead of border for an inset look
    // eslint-disable-next-line  @typescript-eslint/no-unused-vars
    borderWidth,
    // eslint-disable-next-line  @typescript-eslint/no-unused-vars
    borderStyle,
    ...restContainerStyles
  } = containerStyles;
  const {
    // eslint-disable-next-line  @typescript-eslint/no-unused-vars
    fontFamily, // we apply font family thru css modules
    ...restInputStyles
  } = inputStyles;

  return {
    container: {
      ...restContainerStyles,
      backgroundColor: getTextfieldStyleProp(
        convertedAppearance as TextfieldAppearance,
        state,
        'backgroundColor',
      ),
      border: 'none',
      boxShadow: getTextfieldStyleProp(
        convertedAppearance as TextfieldAppearance,
        state,
        'boxShadow',
      ),
      color: getTextfieldStyleProp(
        convertedAppearance as TextfieldAppearance,
        state,
        'color',
      ),
      cursor: getTextfieldStyleProp(
        convertedAppearance as TextfieldAppearance,
        state,
        'cursor',
      ),
      lineHeight: getTextfieldStyleProp(
        convertedAppearance as TextfieldAppearance,
        state,
        'lineHeight',
      ),
      transition: getTextfieldStyleProp(
        convertedAppearance as TextfieldAppearance,
        state,
        'transition',
      ),
      maxHeight: TextfieldDefaultMaxHeight,
      // input handles padding styles
      padding: '0',
    },
    input: {
      ...restInputStyles,
      padding: getTextfieldStyleProp(
        convertedAppearance as TextfieldAppearance,
        state,
        'padding',
      ),
      // hack to style the placeholder, this overwrites the pseudoselector
      // being used in ADG Textfield Theme
      '&::placeholder': {
        color: getTextfieldStyleProp(
          convertedAppearance as TextfieldAppearance,
          DEFAULT,
          'color',
          textfieldPlaceholderStyleDict,
        ),
      },
    },
  };
};

type Omit<T, K> = Pick<T, Exclude<keyof T, K>>;
interface BaseTextfieldProps
  extends Omit<AllHTMLAttributes<HTMLInputElement>, 'label' | 'onChange'> {
  /**
   * A string of classnames to be applied
   *
   * @default ''
   **/
  className?: string;
  style?: CSSProperties;
  /**
   * Determines the appearance of the text field
   * @default default
   */
  appearance?: TextfieldAppearance | 'standard';
  /**
   * Sets the field as uneditable, with a changed hover state.
   * @default false
   */
  isDisabled?: boolean;
  /**
   * Sets styling to indicate that the input is invalid
   * @default false
   */
  isInvalid?: boolean;
  /**
   * Sets content text value to monospace
   * @default false
   */
  isMonospaced?: boolean;
  /**
   * If true, prevents the value of the input from being edited.
   * @default false
   */
  isReadOnly?: boolean;
  /**
   * Set required for form that the field is part of.
   * @default false
   */
  isRequired?: boolean;
  /**
   * Element after input inside textfield.
   * @default null
   */
  elemAfterInput?: React.ReactNode;
  /**
   * Element before input inside textfield.
   * @default null
   */
  elemBeforeInput?: React.ReactNode;
  /**
   * Sets maximum width of input
   * @default "100%"
   */
  width?: TextfieldWidthValue | string | number;
  /** Mousedown handler that will fire on the container element */
  onMouseDown?: React.MouseEventHandler<HTMLElement>;
  /** onChange handler */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export type TextfieldProps = BaseTextfieldProps & {
  /**
   * Label: can either be a React node or a string that will be put in a <span>
   * @default null
   */
  label?: TextfieldLabelType;
  /**
   * Placeholder: a placeholder text that appears inside input if no text is there
   * @default null
   */
  placeholder?: string;
};

export const Textfield: React.FunctionComponent<TextfieldProps> = (props) => {
  const { appearance, children, className, label, ...rest } = props;
  const textfieldClassName = cx(
    {
      [TextfieldClasses.BASE]: true,
      [styles[TextfieldClasses.BASE]]: true,
    },
    className,
  );

  let textfieldName = props.name;
  // for now, if no name is provided, use base classname
  if (!textfieldName) {
    textfieldName = styles[TextfieldClasses.BASE];
  }

  let labelEl: TextfieldLabelType = label;
  if (typeof label === 'string') {
    labelEl = (
      <label htmlFor={textfieldName} className={styles[TextfieldClasses.LABEL]}>
        {label}
      </label>
    );
  }
  // weird little hack to convert usage of "default" appearance to "standard"
  // we typically use "default" as our baseline appearance value, and this
  // component uses "standard", so to keep with consistency, we intercept the
  // "default" value and assign it to "standard"
  // NOTE: In the theme function, we do the opposite conversion
  let convertedAppearance:
    | TextfieldAppearance
    | AkTextfieldAppearance
    | undefined = appearance;
  if (appearance === DEFAULT) {
    convertedAppearance = STANDARD;
  }

  return (
    <>
      {labelEl}
      <AkTextfield
        appearance={convertedAppearance as AkTextfieldAppearance}
        theme={customTheme}
        className={textfieldClassName}
        name={textfieldName}
        {...rest}
      >
        {children}
      </AkTextfield>
    </>
  );
};
